import {io, Socket} from "socket.io-client";
import {ref, Ref} from "vue";
import useStore from "../store.ts";
import {ConnectionState} from "./ConnectionState.ts";
import {RecoveryHandler} from "./RecoveryHandler.ts";
import {SocketListener} from "./SocketListener.ts";
import type {SocketManager} from "./SocketManager.ts";
import type {LayoutManager} from "../layout/LayoutManager.ts";
import type {ChannelManager} from "../channel/ChannelManager.ts";
import type {RuleManager} from "../rule/RuleManager.ts";
import type {PlaylistManager} from "../playlist/PlaylistManager.ts";
import {postRenderRequest} from "../worker/RenderWorkerManager.ts";
import {
    DisconnectPolicyAction,
    DisconnectPolicyActionSource,
    DisconnectPolicyActionType,
} from "../types.ts";
import {buildAssetUrl, isAssetCached} from "../utils/AssetUrl.ts";
import {CacheManager} from "../cache/CacheManager.ts";

/* see docs/EXTRACTED_COMMENTS.md#c105 */
export enum SupervisorState {
    Idle = "idle",
    Connecting = "connecting",
    Authenticating = "authenticating",
    Syncing = "syncing",
    Live = "live",
    Degraded = "degraded",
}

export type SupervisorDeps = {
    url: string;
    socketManager: SocketManager;
    getLayoutManager: () => LayoutManager | null;
    getChannelManager: () => ChannelManager;
    getRuleManager: () => RuleManager;
    getPlaylistManager: () => PlaylistManager;
};

/* see docs/EXTRACTED_COMMENTS.md#c106 */
const CONNECT_TIMEOUT_MS = 30_000;
const AUTH_TIMEOUT_MS = 30_000;
const SYNC_TIMEOUT_MS = 10_000;
/* see docs/EXTRACTED_COMMENTS.md#c107 */
const SOCKET_HANDSHAKE_TIMEOUT_MS = CONNECT_TIMEOUT_MS;

/* see docs/EXTRACTED_COMMENTS.md#c108 */
const BACKOFF_CAP_MS = 30_000;

/* see docs/EXTRACTED_COMMENTS.md#c109 */
const MAX_ATTEMPTS_BEFORE_RELOAD = 30;

const RELOAD_COOLDOWN_MS = 30 * 60_000;
const RELOAD_COOLDOWN_KEY = "lumids:lastSupervisorReload";

function computeBackoff(attempt: number): number {
    return Math.min(BACKOFF_CAP_MS, 1000 * Math.pow(2, Math.max(0, attempt - 1)));
}

/* see docs/EXTRACTED_COMMENTS.md#c110 */
export class ConnectionSupervisor implements RecoveryHandler {
    public readonly state: Ref<SupervisorState> = ref(SupervisorState.Idle);
    public readonly attempt: Ref<number> = ref(0);
    public readonly nextRetryAt: Ref<number | null> = ref(null);
    public readonly lastError: Ref<string | null> = ref(null);

    private socket: Socket | null = null;
    private transitionTimer: ReturnType<typeof setTimeout> | null = null;
    private retryTimer: ReturnType<typeof setTimeout> | null = null;
    /* see docs/EXTRACTED_COMMENTS.md#c111 */
    private disconnectStartedAt: number | null = null;
    private disconnectPolicyTimers: ReturnType<typeof setTimeout>[] = [];
    private disposed = false;
    private started = false;

    constructor(private readonly deps: SupervisorDeps) {}

    /* ----------------------------------------------------------------------
     * Public lifecycle
     * ------------------------------------------------------------------- */

    public start(): void {
        if (this.started || this.disposed) return;
        this.started = true;
        console.log("[Supervisor] start()");
        // see docs/EXTRACTED_COMMENTS.md#c112
        this.enterConnecting("start");
    }

    /* see docs/EXTRACTED_COMMENTS.md#c113 */
    public requestImmediateRetry(reason: string): void {
        if (this.disposed) return;
        if (this.state.value !== SupervisorState.Degraded) {
            console.log(`[Supervisor] immediate retry ignored (${reason}); state=${this.state.value}`);
            return;
        }
        console.log(`[Supervisor] immediate retry (${reason})`);
        if (this.retryTimer != null) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
        this.nextRetryAt.value = null;
        this.enterConnecting(`immediate:${reason}`);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c436 */
    public recycle(reason: string): void {
        if (this.disposed) return;
        if (this.state.value === SupervisorState.Connecting) {
            console.log(`[Supervisor] recycle(${reason}) ignored — already CONNECTING`);
            return;
        }
        console.warn(`[Supervisor] recycle requested: ${reason}`);
        this.enterDegraded(`recycle:${reason}`);
    }

    public dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.clearTransitionTimer();
        if (this.retryTimer != null) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
        this.clearDisconnectPolicyEffects();
        this.teardownSocket();
    }

    public getSocket(): Socket | null {
        return this.socket;
    }

    public isLive(): boolean {
        return this.state.value === SupervisorState.Live;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c437 */

    public async handleConnect(): Promise<void> {
        if (this.disposed) return;
        if (this.state.value !== SupervisorState.Connecting) {
            console.log(`[Supervisor] stray 'connect' in state ${this.state.value} — ignoring`);
            return;
        }
        console.log("[Supervisor] socket connected → AUTHENTICATING");
        this.clearTransitionTimer();
        this.setState(SupervisorState.Authenticating, ConnectionState.Authenticating);
        this.armTransitionTimeout("authenticate_timeout", AUTH_TIMEOUT_MS);

        const sm = this.deps.socketManager;
        sm.setAuthenticated(false);
        const deviceId = sm.getDeviceId();
        if (deviceId && deviceId.length > 0) {
            await sm.sendDevice(deviceId);
        } else {
            console.warn("[Supervisor] no device_id at handleConnect — auth will not complete");
        }
    }

    public handleDisconnect(reason?: string): void {
        if (this.disposed) return;
        console.warn(`[Supervisor] socket disconnect: ${reason ?? ""}`);
        this.enterDegraded(reason ?? "disconnect");
    }

    public handleConnectError(error: unknown): void {
        this.handleTransportError(error);
    }

    public handleTransportError(error: unknown): void {
        if (this.disposed) return;
        const msg = typeof error === "string"
            ? error
            : (typeof (error as any)?.message === "string" ? (error as any).message : "transport_error");
        console.warn(`[Supervisor] transport error: ${msg}`);
        this.lastError.value = msg;

        // see docs/EXTRACTED_COMMENTS.md#c438
        if (msg.startsWith("Maximum number of connections for ")) {
            const store = useStore();
            store.error = msg;
            // see docs/EXTRACTED_COMMENTS.md#c439
            this.teardownSocket();
            this.setState(SupervisorState.Degraded, ConnectionState.Degraded);
            setTimeout(() => document.location.reload(), 15_000);
            return;
        }

        this.enterDegraded(msg);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c440 */
    public bumpConnectionState(connState: ConnectionState, lastError: string | null = null): void {
        if (this.disposed) return;
        if (connState === ConnectionState.Ready && this.state.value === SupervisorState.Authenticating) {
            this.lastError.value = lastError ?? null;
            this.onAuthSucceeded();
            return;
        }
        const store = useStore();
        store.connection.state = connState;
    }

    /* ----------------------------------------------------------------------
     * Internal transitions
     * ------------------------------------------------------------------- */

    private enterConnecting(reason: string): void {
        if (this.disposed) return;
        this.attempt.value += 1;
        console.log(`[Supervisor] → CONNECTING (${reason}); attempt ${this.attempt.value}`);
        this.setState(SupervisorState.Connecting, ConnectionState.Connecting);
        this.lastError.value = null;
        this.teardownSocket();
        this.buildSocket();
        this.armTransitionTimeout("connect_timeout", CONNECT_TIMEOUT_MS);
    }

    private onAuthSucceeded(): void {
        if (this.disposed) return;
        console.log("[Supervisor] auth succeeded → SYNCING");
        this.clearTransitionTimer();
        this.setState(SupervisorState.Syncing, ConnectionState.Syncing);
        this.armTransitionTimeout("sync_timeout", SYNC_TIMEOUT_MS);

        const store = useStore();

        // see docs/EXTRACTED_COMMENTS.md#c441
        this.deps.socketManager.socketListener?.resetInitialLoadState(false);
        // Every channel goes back to pending: replayed rows apply silently and
        // each channel's post-announce finalize re-syncs its subscriptions.
        this.deps.getChannelManager().markAllPending();

        if (this.deps.socketManager.socketListener?.isSubscriptionsActivated()) {
            this.deps.socketManager.socketListener?.markSubscriptionsActivated();
        }

        // see docs/EXTRACTED_COMMENTS.md#c442
        if (Object.keys(store.components).length > 0) {
            this.deps.socketManager.socketListener?.markSubscriptionsActivated();
        }

        // see docs/EXTRACTED_COMMENTS.md#c443
        this.deps.getRuleManager().syncToServer();
        this.deps.getPlaylistManager().syncToServer();

        const layoutManager = this.deps.getLayoutManager();
        if (layoutManager != null) {
            void layoutManager.requestLayoutHash();
        }

        // see docs/EXTRACTED_COMMENTS.md#c729
        if (store.layout != null) {
            void postRenderRequest(["reconnectSync"], [String(store.layout)]);
        }

        // see docs/EXTRACTED_COMMENTS.md#c446
        this.enterLive("sync_dispatched");
    }

    private enterLive(reason: string): void {
        if (this.disposed) return;
        console.log(`[Supervisor] → LIVE (${reason})`);
        this.clearTransitionTimer();
        this.attempt.value = 0;
        this.nextRetryAt.value = null;
        this.lastError.value = null;
        this.clearDisconnectPolicyEffects();
        this.disconnectStartedAt = null;
        // Anchor the "connected for…" duration chip in the StatusBar.
        const store = useStore();
        store.connection.connectedAt = Date.now();
        store.connection.lastDisconnectedAt = null;
        this.setState(SupervisorState.Live, ConnectionState.Ready);
        // see docs/EXTRACTED_COMMENTS.md#c447
        this.deps.socketManager.getUpdateQueueManager().notifyLive();

        // see docs/EXTRACTED_COMMENTS.md#c448
        CacheManager.instance.markDirty();
        void CacheManager.instance.commitSnapshot("enter-live");
    }

    private enterDegraded(reason: string): void {
        if (this.disposed) return;
        console.warn(`[Supervisor] → DEGRADED (${reason})`);
        this.clearTransitionTimer();
        // see docs/EXTRACTED_COMMENTS.md#c449
        this.teardownSocket();
        this.lastError.value = reason;
        this.deps.socketManager.resetAuthenticationState();
        // see docs/EXTRACTED_COMMENTS.md#c717
        this.deps.getChannelManager().markFullResyncNeeded();
        // see docs/EXTRACTED_COMMENTS.md#c450
        const store = useStore();
        if (store.connection.lastDisconnectedAt == null) {
            store.connection.lastDisconnectedAt = Date.now();
        }
        store.connection.connectedAt = null;
        this.setState(SupervisorState.Degraded, ConnectionState.Disconnected);
        // see docs/EXTRACTED_COMMENTS.md#c451
        if (this.disconnectStartedAt == null) {
            this.disconnectStartedAt = Date.now();
            this.scheduleDisconnectPolicy();
        }
        this.scheduleNextRetry();
    }

    private scheduleNextRetry(): void {
        if (this.disposed) return;

        // see docs/EXTRACTED_COMMENTS.md#c452
        if (this.retryTimer != null) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }

        if (this.attempt.value >= MAX_ATTEMPTS_BEFORE_RELOAD) {
            this.escalateToReload();
            return;
        }

        const delay = computeBackoff(Math.max(1, this.attempt.value));
        const fireAt = Date.now() + delay;
        this.nextRetryAt.value = fireAt;
        console.log(`[Supervisor] next retry in ${delay}ms (attempt ${this.attempt.value})`);

        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            if (this.disposed) return;
            if (this.state.value !== SupervisorState.Degraded) return;
            this.enterConnecting("backoff_due");
        }, delay);
    }

    private escalateToReload(): void {
        if (this.disposed) return;
        const now = Date.now();
        let last = 0;
        try {
            const raw = window.localStorage.getItem(RELOAD_COOLDOWN_KEY);
            const parsed = raw ? Number.parseInt(raw, 10) : 0;
            last = Number.isFinite(parsed) ? parsed : 0;
        } catch {
            last = 0;
        }
        if (now - last < RELOAD_COOLDOWN_MS) {
            console.warn("[Supervisor] reload escalation suppressed by cooldown — continuing to retry indefinitely");
            // see docs/EXTRACTED_COMMENTS.md#c453
            this.attempt.value = MAX_ATTEMPTS_BEFORE_RELOAD - 1;
            this.scheduleNextRetry();
            return;
        }
        try {
            window.localStorage.setItem(RELOAD_COOLDOWN_KEY, String(now));
        } catch {
            // see docs/EXTRACTED_COMMENTS.md#c454
        }
        console.error("[Supervisor] escalating to force-reload");
        window.dispatchEvent(new Event("app:force-reload"));
    }

    /* see docs/EXTRACTED_COMMENTS.md#c455 */

    private buildSocket(): void {
        if (this.disposed) return;
        console.log(`[Supervisor] building fresh socket → ${this.deps.url}`);
        const socket = io(this.deps.url, {
            timeout: SOCKET_HANDSHAKE_TIMEOUT_MS,
            transports: ["websocket"],
            requestTimeout: SOCKET_HANDSHAKE_TIMEOUT_MS,
            reconnection: false,
            retries: 0,
        });
        this.socket = socket;
        this.deps.socketManager.adoptSocket(socket);
        // see docs/EXTRACTED_COMMENTS.md#c456
        this.deps.socketManager.commandQueue.reset();

        const listener = new SocketListener(socket, this.deps.socketManager, this);
        listener.loadEvents();
        this.deps.socketManager.adoptListener(listener);
    }

    private teardownSocket(): void {
        if (this.socket != null) {
            try {
                this.socket.removeAllListeners();
                this.socket.disconnect();
            } catch (e) {
                console.warn("[Supervisor] socket teardown error", e);
            }
        }
        this.socket = null;
    }

    /* ----------------------------------------------------------------------
     * Helpers
     * ------------------------------------------------------------------- */

    private setState(supState: SupervisorState, connState: ConnectionState): void {
        this.state.value = supState;
        const store = useStore();
        store.connection.state = connState;
    }

    private armTransitionTimeout(reason: string, durationMs: number): void {
        this.clearTransitionTimer();
        this.transitionTimer = setTimeout(() => {
            this.transitionTimer = null;
            if (this.disposed) return;
            console.warn(`[Supervisor] transition timeout: ${reason}`);
            this.enterDegraded(reason);
        }, durationMs);
    }

    private clearTransitionTimer(): void {
        if (this.transitionTimer != null) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = null;
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c457 */

    private scheduleDisconnectPolicy(): void {
        const store = useStore();
        const policy = store.device_settings?.disconnect_policy;
        if (!policy) return;
        this.scheduleDisconnectPolicyAction(policy.assign_action, "assign");
        this.scheduleDisconnectPolicyAction(policy.reboot_action, "reboot");
    }

    private scheduleDisconnectPolicyAction(
        action: DisconnectPolicyAction | undefined,
        source: DisconnectPolicyActionSource,
    ): void {
        if (!action) return;
        const delayMs = this.normalizeAfterDurationMs(action.after_duration);
        const timerId = setTimeout(() => {
            void this.applyDisconnectPolicyAction(action, source);
        }, delayMs);
        this.disconnectPolicyTimers.push(timerId);
    }

    private async applyDisconnectPolicyAction(
        action: DisconnectPolicyAction,
        source: DisconnectPolicyActionSource,
    ): Promise<void> {
        if (this.disposed) return;
        if (this.state.value !== SupervisorState.Degraded) return;
        const resolved = await this.resolveDisconnectPolicyAction(action);
        if (!resolved) return;
        const store = useStore();
        if (resolved.action_type === DisconnectPolicyActionType.IMAGE) {
            const imageUrl = this.getDisconnectPolicyImageUrl(resolved);
            if (!imageUrl) return;
            store.showDisconnectPolicyDisplay({
                text: resolved.text ?? null,
                image_url: imageUrl,
                source,
                activated_at: Date.now(),
            });
            return;
        }
        if (resolved.action_type === DisconnectPolicyActionType.TEXT) {
            if (!resolved.text) return;
            store.showDisconnectPolicyDisplay({
                text: resolved.text,
                image_url: null,
                source,
                activated_at: Date.now(),
            });
        }
        // see docs/EXTRACTED_COMMENTS.md#c458
    }

    /* see docs/EXTRACTED_COMMENTS.md#c459 */
    private async resolveDisconnectPolicyAction(
        action: DisconnectPolicyAction,
    ): Promise<DisconnectPolicyAction | null> {
        if (action.text) {
            return {...action, action_type: DisconnectPolicyActionType.TEXT};
        }
        const imageUrl = this.getDisconnectPolicyImageUrl(action);
        if (imageUrl && await isAssetCached(imageUrl)) {
            return {...action, action_type: DisconnectPolicyActionType.IMAGE};
        }
        return null;
    }

    private getDisconnectPolicyImageUrl(action: DisconnectPolicyAction): string | null {
        const store = useStore();
        const baseUrl = store.device_settings?.content_url ?? "";
        const url = buildAssetUrl(action.image_url, baseUrl);
        return url || null;
    }

    private normalizeAfterDurationMs(afterDuration?: number | null): number {
        if (!afterDuration || afterDuration <= 0) return 0;
        // see docs/EXTRACTED_COMMENTS.md#c460
        return afterDuration < 1000 ? afterDuration * 100 : afterDuration;
    }

    private clearDisconnectPolicyEffects(): void {
        if (this.disconnectPolicyTimers.length > 0) {
            for (const id of this.disconnectPolicyTimers) clearTimeout(id);
            this.disconnectPolicyTimers = [];
        }
        const store = useStore();
        store.clearDisconnectPolicyDisplay();
    }
}
